import { ApiError, ERROR_CODES, type ApiErrorDetails } from '@luacentrix/shared';
import {
  BUILD_JOB_STATUSES,
  CHECKPOINT_STATUSES,
  KEYSYSTEM_PROVIDERS,
  KEYSYSTEM_STATUSES,
  LICENSE_STATUSES,
  LICENSE_TYPES,
  ORDER_STATUSES,
  PAYMENT_STATUSES,
  PROJECT_STATUSES,
  PROVIDER_IDS,
  REFUND_STATUSES,
  SCRIPT_UPLOAD_STATUSES,
  SCRIPT_ENTRY_STATUSES,
  SCRIPT_OBFUSCATION_LIMITS,
  SCRIPT_OBFUSCATION_PRESETS,
  SECURITY_EVENT_SEVERITIES,
  STATUS_INCIDENT_STATES,
  STATUS_OVERALL_STATES,
  STATUS_PROBES,
  STATUS_SEVERITIES,
  STATUS_STATES,
  UPLOAD_ALLOWED_EXTENSIONS,
  type AuthIdentity,
  type BuildLoaderResponse,
  type DeleteScriptResponse,
  type DiscardScriptUploadResponse,
  type KeysystemConfigResponse,
  type License,
  type LoaderScript,
  type ObfuscateScriptResponse,
  type Order,
  type Paginated,
  type Project,
  type ProtectedScript,
  type ScriptEntry,
  type ScriptListResponse,
  type ScriptLoader,
  type ScriptProtectionStats,
  type ScriptResponse,
  type ScriptUpload,
  type ScriptUploadResponse,
  type StatusComponent,
  type StatusIncident,
  type StatusResponse,
} from '@luacentrix/contracts';
import {
  BLOCK_KINDS,
  DETECTION_SOURCES,
  MAX_NOTIFICATIONS_PER_WINDOW_LIMIT,
  MAX_NOTIFICATION_WINDOW_MS,
  MIN_NOTIFICATION_WINDOW_MS,
  NOTIFICATION_DELIVERY_STATUSES,
  type BlockPageConfig,
  type IpBlockRecord,
  type IpMatch,
  type SecurityAdminAction,
  type SecurityNotificationRecord,
  type SecurityPolicy,
} from '@luacentrix/security';
import type {
  AccountSessionResponse,
  SecurityBlockPagePreviewResponse,
  SecurityIpQueryResponse,
  SecurityNotificationsResponse,
  SecurityStatsResponse,
  SecurityStatusResponse,
} from './security';
import {
  SECURITY_NOTIFICATION_KINDS,
  SECURITY_NOTIFICATION_STATUSES,
  SECURITY_STATUS_STATES,
} from './security';
import { discordAvatarUrlFromCdnUrl } from './discord-avatar';

export const IP_MATCH_TYPES = ['ip', 'cidr'] as const;

export const SECURITY_BLOCK_STATUSES = ['active', 'revoked', 'expired'] as const;

export const CREDENTIAL_KINDS = ['session', 'api_key'] as const;

export const ADMIN_TARGET_KINDS = ['ip_block', 'allowlist', 'policy', 'event_review'] as const;

export const TRUSTED_SERVER_FIELDS = [
  'license.status',
  'order.status',
  'order.payment_status',
  'checkpoint.status',
  'build_job.status',
  'refund.status',
  'provider.id',
] as const;

export interface GuardContext {
  field: (typeof TRUSTED_SERVER_FIELDS)[number] | string;
  requestId?: string | undefined;
}

function guardFailure(value: unknown, context: GuardContext): never {
  const details: ApiErrorDetails = {
    field: context.field,
    received: typeof value === 'string' ? value.slice(0, 64) : String(value),
    allowed_values: [],
  };
  throw new ApiError(ERROR_CODES.UNSUPPORTED_SERVER_STATE, {
    details:
      context.requestId === undefined ? details : { ...details, request_id: context.requestId },
  });
}

export function requireKnown<T extends string>(
  value: unknown,
  allowed: readonly T[],
  context: GuardContext,
): T {
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) {
    guardFailure(value, context);
  }
  return value as T;
}

export function requireObjectShape(value: unknown, context: GuardContext): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    guardFailure(value, context);
  }
  return value as Record<string, unknown>;
}

export function requireNumberShape(value: unknown, context: GuardContext): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    guardFailure(value, context);
  }
  return value;
}

export function requireNullableNumber(value: unknown, context: GuardContext): number | null {
  if (value === null) {
    return null;
  }
  return requireNumberShape(value, context);
}

export function assertServerTimestamp(value: unknown, context: GuardContext): number {
  const timestamp = requireNumberShape(value, context);
  if (!Number.isSafeInteger(timestamp) || timestamp < 0) {
    guardFailure(value, context);
  }
  return timestamp;
}

export function assertProjectStatus(project: Project, context: GuardContext): Project {
  requireKnown(project.status, PROJECT_STATUSES, { ...context, field: 'project.status' });
  assertServerTimestamp(project.created_at, { ...context, field: 'project.created_at' });
  assertServerTimestamp(project.updated_at, { ...context, field: 'project.updated_at' });
  if (typeof project.id !== 'string' || !project.id.startsWith('proj_')) {
    guardFailure(project.id, { ...context, field: 'project.id' });
  }
  return project;
}

export function assertProjectPage(
  page: Paginated<Project>,
  context: GuardContext,
): Paginated<Project> {
  for (const project of page.items) {
    assertProjectStatus(project, context);
  }
  requireNumberShape(page.limit, context);
  if (page.next_cursor !== null && typeof page.next_cursor !== 'string') {
    guardFailure(page.next_cursor, context);
  }
  return page;
}

export function assertLicenseStatus(license: License, context: GuardContext): License {
  requireKnown(license.status, LICENSE_STATUSES, { ...context, field: 'license.status' });
  requireKnown(license.license_type, LICENSE_TYPES, {
    ...context,
    field: 'license.license_type',
  });
  assertServerTimestamp(license.created_at, { ...context, field: 'license.created_at' });
  assertServerTimestamp(license.updated_at, { ...context, field: 'license.updated_at' });
  if (typeof license.id !== 'string' || !license.id.startsWith('lic_')) {
    guardFailure(license.id, { ...context, field: 'license.id' });
  }
  if ('secret' in license || 'plaintext_secret' in license || 'secret_hash' in license) {
    guardFailure('secret bearing license payload', context);
  }
  return license;
}

export function assertLicensePage(
  page: Paginated<License>,
  context: GuardContext,
): Paginated<License> {
  for (const license of page.items) {
    assertLicenseStatus(license, context);
  }
  requireNumberShape(page.limit, context);
  if (page.next_cursor !== null && typeof page.next_cursor !== 'string') {
    guardFailure(page.next_cursor, context);
  }
  return page;
}

export function assertOrderStatus(order: Order, context: GuardContext): Order {
  requireKnown(order.status, ORDER_STATUSES, { ...context, field: 'order.status' });
  requireKnown(order.payment_status, PAYMENT_STATUSES, {
    ...context,
    field: 'order.payment_status',
  });
  return order;
}

export function assertCheckpointStatus(value: unknown, context: GuardContext): void {
  requireKnown(value, CHECKPOINT_STATUSES, { ...context, field: 'checkpoint.status' });
}

export function assertProvider(value: unknown, context: GuardContext): void {
  requireKnown(value, PROVIDER_IDS, { ...context, field: 'provider.id' });
}

export function assertBuildJobStatus(value: unknown, context: GuardContext): void {
  requireKnown(value, BUILD_JOB_STATUSES, { ...context, field: 'build_job.status' });
}

export function assertRefundStatus(value: unknown, context: GuardContext): void {
  requireKnown(value, REFUND_STATUSES, { ...context, field: 'refund.status' });
}

export function assertNoAuthorityOverServerState<T>(
  candidate: T,
  serverValue: T,
  context: GuardContext,
): T {
  if (candidate !== serverValue) {
    guardFailure(candidate, context);
  }
  return serverValue;
}

export function assertIpMatch(match: IpMatch, context: GuardContext): IpMatch {
  if (match === null || typeof match !== 'object') {
    guardFailure(match, context);
  }
  requireKnown(match.type, IP_MATCH_TYPES, { ...context, field: 'security.match.type' });
  if (typeof match.value !== 'string' || match.value.trim() === '') {
    guardFailure(match.value, { ...context, field: 'security.match.value' });
  }
  if (match.type === 'ip' && match.value.includes('/')) {
    guardFailure(match.value, { ...context, field: 'security.match.value' });
  }
  if (match.type === 'cidr' && !match.value.includes('/')) {
    guardFailure(match.value, { ...context, field: 'security.match.value' });
  }
  return match;
}

export function assertSecurityBlock(block: IpBlockRecord, context: GuardContext): IpBlockRecord {
  requireKnown(block.kind, BLOCK_KINDS, { ...context, field: 'security.block.kind' });
  requireKnown(block.status, SECURITY_BLOCK_STATUSES, {
    ...context,
    field: 'security.block.status',
  });
  requireKnown(block.detection_source, DETECTION_SOURCES, {
    ...context,
    field: 'security.block.detection_source',
  });
  assertIpMatch(block.match, context);
  if (typeof block.reference !== 'string' || block.reference === '') {
    guardFailure(block.reference, { ...context, field: 'security.block.reference' });
  }
  assertServerTimestamp(block.last_detected_at, { ...context, field: 'security.block.last_seen' });
  if (block.kind === 'temporary' && block.status === 'active' && block.expires_at === null) {
    guardFailure(null, { ...context, field: 'security.block.expires_at' });
  }
  if (block.kind === 'permanent' && block.expires_at !== null) {
    guardFailure(block.expires_at, { ...context, field: 'security.block.expires_at' });
  }
  if (block.expires_at !== null) {
    assertServerTimestamp(block.expires_at, { ...context, field: 'security.block.expires_at' });
  }
  return block;
}

export function assertSecurityBlockPage(
  page: Paginated<IpBlockRecord>,
  context: GuardContext,
): Paginated<IpBlockRecord> {
  for (const block of page.items) {
    assertSecurityBlock(block, context);
  }
  requireNumberShape(page.limit, context);
  if (page.next_cursor !== null && typeof page.next_cursor !== 'string') {
    guardFailure(page.next_cursor, context);
  }
  return page;
}

export function assertSecurityEventPage<T extends { severity: string }>(
  page: Paginated<T>,
  context: GuardContext,
): Paginated<T> {
  for (const event of page.items) {
    requireKnown(event.severity, SECURITY_EVENT_SEVERITIES, {
      ...context,
      field: 'security.event.severity',
    });
  }
  requireNumberShape(page.limit, context);
  return page;
}

export function assertSecurityActionPage(
  page: Paginated<SecurityAdminAction>,
  context: GuardContext,
): Paginated<SecurityAdminAction> {
  for (const action of page.items) {
    requireKnown(action.target_kind, ADMIN_TARGET_KINDS, {
      ...context,
      field: 'security.action.target_kind',
    });
    if (typeof action.reason !== 'string') {
      guardFailure(action.reason, { ...context, field: 'security.action.reason' });
    }
    assertServerTimestamp(action.created_at, { ...context, field: 'security.action.created_at' });
  }
  requireNumberShape(page.limit, context);
  return page;
}

export function assertSecurityStatus(
  status: SecurityStatusResponse,
  context: GuardContext,
): SecurityStatusResponse {
  requireKnown(status.status, SECURITY_STATUS_STATES, {
    ...context,
    field: 'security.status.state',
  });
  if (status.kind !== null) {
    requireKnown(status.kind, BLOCK_KINDS, { ...context, field: 'security.status.kind' });
  }
  if (typeof status.reference !== 'string' || status.reference === '') {
    guardFailure(status.reference, { ...context, field: 'security.status.reference' });
  }
  if (status.expires_at !== null) {
    assertServerTimestamp(status.expires_at, { ...context, field: 'security.status.expires_at' });
  }
  if (status.restricted && status.kind === 'temporary' && status.expires_at === null) {
    guardFailure(null, { ...context, field: 'security.status.expires_at' });
  }
  if (status.block_page !== null) {
    assertBlockPageCopy(status.block_page, context);
  }
  return status;
}

export function assertSecurityIpQuery(
  query: SecurityIpQueryResponse,
  context: GuardContext,
): SecurityIpQueryResponse {
  if (query.blocked && query.block === null) {
    guardFailure(null, { ...context, field: 'security.ip.block' });
  }
  if (query.allowlisted && query.allowlist === null) {
    guardFailure(null, { ...context, field: 'security.ip.allowlist' });
  }
  if (query.block !== null) {
    assertSecurityBlock(query.block, context);
  }
  requireKnown(query.family, ['ipv4', 'ipv6'] as const, {
    ...context,
    field: 'security.ip.family',
  });
  return query;
}

export function assertSessionIdentity(identity: AuthIdentity, context: GuardContext): AuthIdentity {
  requireKnown(identity.kind, CREDENTIAL_KINDS, { ...context, field: 'session.kind' });
  if (!Array.isArray(identity.permissions) || !Array.isArray(identity.denied_permissions)) {
    guardFailure('non-array permission set', { ...context, field: 'session.permissions' });
  }
  assertServerTimestamp(identity.issued_at, { ...context, field: 'session.issued_at' });
  requireNullableNumber(identity.expires_at, { ...context, field: 'session.expires_at' });
  if (
    identity.expires_at !== null &&
    identity.expires_at !== 0 &&
    identity.expires_at <= identity.issued_at
  ) {
    guardFailure(identity.expires_at, { ...context, field: 'session.expires_at' });
  }
  return identity;
}

const SESSION_ACCOUNT_NULLABLE_FIELDS = ['username', 'discord_user_id', 'avatar_url'] as const;

export function assertAccountSession(
  response: AccountSessionResponse,
  context: GuardContext,
): AccountSessionResponse {
  assertSessionIdentity(response.identity, { ...context, field: 'session.identity' });
  const account = response.account;
  if (account === null || account === undefined) {
    return response;
  }
  requireObjectShape(account, { ...context, field: 'session.account' });
  if (typeof account.display_name !== 'string') {
    guardFailure(account.display_name, { ...context, field: 'session.account.display_name' });
  }
  if (typeof account.linked !== 'boolean') {
    guardFailure(account.linked, { ...context, field: 'session.account.linked' });
  }
  for (const key of SESSION_ACCOUNT_NULLABLE_FIELDS) {
    const value = account[key];
    if (value !== null && typeof value !== 'string') {
      guardFailure(value, { ...context, field: `session.account.${key}` });
    }
  }
  if (account.linked && account.discord_user_id === null) {
    guardFailure(null, { ...context, field: 'session.account.discord_user_id' });
  }
  if (!account.linked && (account.username !== null || account.avatar_url !== null)) {
    guardFailure(account.username, { ...context, field: 'session.account.username' });
  }
  const avatarHash = account.avatar_hash;
  if (avatarHash !== undefined && avatarHash !== null && typeof avatarHash !== 'string') {
    guardFailure(avatarHash, { ...context, field: 'session.account.avatar_hash' });
  }
  if (
    account.avatar_url !== null &&
    discordAvatarUrlFromCdnUrl(
      account.avatar_url,
      typeof account.discord_user_id === 'string' ? account.discord_user_id : null,
    ) === null
  ) {
    guardFailure(account.avatar_url, { ...context, field: 'session.account.avatar_url' });
  }
  return response;
}

export function assertSecurityStats(
  stats: SecurityStatsResponse,
  context: GuardContext,
): SecurityStatsResponse {
  for (const key of [
    'total_blocked',
    'active_temporary',
    'active_permanent',
    'expiring_within_ms',
  ] as const) {
    const value = requireNumberShape(stats[key], { ...context, field: `security.stats.${key}` });
    if (value < 0) {
      guardFailure(value, { ...context, field: `security.stats.${key}` });
    }
  }
  if (stats.active_temporary + stats.active_permanent > stats.total_blocked) {
    guardFailure(stats.total_blocked, { ...context, field: 'security.stats.total_blocked' });
  }
  return stats;
}

export function assertNotificationSettings(
  policy: SecurityPolicy,
  context: GuardContext,
): SecurityPolicy {
  requireObjectShape(policy.notifications, { ...context, field: 'security.policy.notifications' });
  const settings = policy.notifications;
  if (typeof settings.enabled !== 'boolean' || typeof settings.discord_enabled !== 'boolean') {
    guardFailure(String(settings.enabled), {
      ...context,
      field: 'security.policy.notifications.enabled',
    });
  }
  requireKnown(settings.min_severity, SECURITY_EVENT_SEVERITIES, {
    ...context,
    field: 'security.policy.notifications.min_severity',
  });
  if (
    !Number.isSafeInteger(settings.window_ms) ||
    settings.window_ms < MIN_NOTIFICATION_WINDOW_MS ||
    settings.window_ms > MAX_NOTIFICATION_WINDOW_MS
  ) {
    guardFailure(settings.window_ms, {
      ...context,
      field: 'security.policy.notifications.window_ms',
    });
  }
  if (
    !Number.isSafeInteger(settings.max_per_window) ||
    settings.max_per_window < 1 ||
    settings.max_per_window > MAX_NOTIFICATIONS_PER_WINDOW_LIMIT
  ) {
    guardFailure(settings.max_per_window, {
      ...context,
      field: 'security.policy.notifications.max_per_window',
    });
  }
  requireObjectShape(settings.kinds, { ...context, field: 'security.policy.notifications.kinds' });
  for (const kind of SECURITY_NOTIFICATION_KINDS) {
    if (typeof settings.kinds[kind] !== 'boolean') {
      guardFailure(String(settings.kinds[kind]), {
        ...context,
        field: `security.policy.notifications.kinds.${kind}`,
      });
    }
  }
  return policy;
}

export function assertSecurityPolicy(
  policy: SecurityPolicy,
  context: GuardContext,
): SecurityPolicy {
  const version = requireNumberShape(policy.version, {
    ...context,
    field: 'security.policy.version',
  });
  if (version < 1) {
    guardFailure(policy.version, { ...context, field: 'security.policy.version' });
  }
  requireObjectShape(policy.rate_limits, { ...context, field: 'security.policy.rate_limits' });
  requireObjectShape(policy.detectors, { ...context, field: 'security.policy.detectors' });
  requireNumberShape(policy.auto_block.min_confidence, {
    ...context,
    field: 'security.policy.min_confidence',
  });
  requireNumberShape(policy.auto_block.min_signals, {
    ...context,
    field: 'security.policy.min_signals',
  });
  requireObjectShape(policy.auto_block.escalation, {
    ...context,
    field: 'security.policy.escalation',
  });
  requireObjectShape(policy.auto_block.duration_by_severity, {
    ...context,
    field: 'security.policy.duration_by_severity',
  });
  assertBlockPageCopy(policy.block_page, context);
  assertNotificationSettings(policy, context);
  return policy;
}

export function assertBlockPageCopy(page: BlockPageConfig, context: GuardContext): BlockPageConfig {
  if (typeof page.title !== 'string' || page.title.trim() === '') {
    guardFailure(page.title, { ...context, field: 'security.block_page.title' });
  }
  if (typeof page.support_url !== 'string' || !page.support_url.startsWith('https://')) {
    guardFailure(page.support_url, { ...context, field: 'security.block_page.support_url' });
  }
  if (page.support_email !== undefined) {
    if (typeof page.support_email !== 'string' || !page.support_email.includes('@')) {
      guardFailure(page.support_email, { ...context, field: 'security.block_page.support_email' });
    }
  }
  return page;
}

export function assertBlockPagePreview(
  preview: SecurityBlockPagePreviewResponse,
  context: GuardContext,
): SecurityBlockPagePreviewResponse {
  requireKnown(preview.kind, BLOCK_KINDS, { ...context, field: 'security.preview.kind' });
  if (typeof preview.reference !== 'string' || preview.reference === '') {
    guardFailure(preview.reference, { ...context, field: 'security.preview.reference' });
  }
  assertBlockPageCopy(preview.block_page, context);
  if (preview.expires_at !== null) {
    assertServerTimestamp(preview.expires_at, { ...context, field: 'security.preview.expires_at' });
  }
  if (preview.kind === 'permanent' && preview.expires_at !== null) {
    guardFailure(preview.expires_at, { ...context, field: 'security.preview.expires_at' });
  }
  return preview;
}

export function assertSecurityNotification(
  record: SecurityNotificationRecord,
  context: GuardContext,
): SecurityNotificationRecord {
  requireKnown(record.kind, SECURITY_NOTIFICATION_KINDS, {
    ...context,
    field: 'security.notification.kind',
  });
  requireKnown(record.status, SECURITY_NOTIFICATION_STATUSES, {
    ...context,
    field: 'security.notification.status',
  });
  requireKnown(record.severity, SECURITY_EVENT_SEVERITIES, {
    ...context,
    field: 'security.notification.severity',
  });
  requireKnown(record.discord_status, NOTIFICATION_DELIVERY_STATUSES, {
    ...context,
    field: 'security.notification.discord_status',
  });
  assertServerTimestamp(record.first_seen_at, {
    ...context,
    field: 'security.notification.first_seen',
  });
  assertServerTimestamp(record.last_seen_at, {
    ...context,
    field: 'security.notification.last_seen',
  });
  assertServerTimestamp(record.window_expires_at, {
    ...context,
    field: 'security.notification.window_expires',
  });
  assertServerTimestamp(record.created_at, {
    ...context,
    field: 'security.notification.created_at',
  });
  const occurrences = requireNumberShape(record.occurrence_count, {
    ...context,
    field: 'security.notification.occurrences',
  });
  if (occurrences < 1) {
    guardFailure(record.occurrence_count, {
      ...context,
      field: 'security.notification.occurrences',
    });
  }
  if (record.last_seen_at < record.first_seen_at) {
    guardFailure(record.last_seen_at, { ...context, field: 'security.notification.last_seen' });
  }
  if (record.window_expires_at <= record.last_seen_at) {
    guardFailure(record.window_expires_at, {
      ...context,
      field: 'security.notification.window_expires',
    });
  }
  if ((record.acknowledged_at === null) !== (record.acknowledged_by === null)) {
    guardFailure(record.acknowledged_at, {
      ...context,
      field: 'security.notification.acknowledged',
    });
  }
  if (record.acknowledged_at !== null) {
    assertServerTimestamp(record.acknowledged_at, {
      ...context,
      field: 'security.notification.acknowledged_at',
    });
  }
  if (record.discord_error_code !== null && record.discord_status !== 'failed') {
    guardFailure(record.discord_error_code, {
      ...context,
      field: 'security.notification.discord_error',
    });
  }
  return record;
}

export function assertSecurityNotificationPage(
  page: SecurityNotificationsResponse,
  context: GuardContext,
): SecurityNotificationsResponse {
  for (const record of page.items) {
    assertSecurityNotification(record, context);
  }
  requireNumberShape(page.limit, context);
  const unread = requireNumberShape(page.unread_count, {
    ...context,
    field: 'security.notification.unread_count',
  });
  if (unread < 0) {
    guardFailure(page.unread_count, { ...context, field: 'security.notification.unread_count' });
  }
  if (page.next_cursor !== null && typeof page.next_cursor !== 'string') {
    guardFailure(page.next_cursor, context);
  }
  return page;
}

const SHA_256_HEX = /^([0-9a-f]){64}$/;

export function assertScriptUpload(upload: ScriptUpload, context: GuardContext): ScriptUpload {
  if (typeof upload.id !== 'string' || upload.id === '') {
    guardFailure(upload.id, { ...context, field: 'script_upload.id' });
  }
  if (typeof upload.filename !== 'string' || upload.filename === '') {
    guardFailure(upload.filename, { ...context, field: 'script_upload.filename' });
  }
  requireKnown(upload.extension, UPLOAD_ALLOWED_EXTENSIONS, {
    ...context,
    field: 'script_upload.extension',
  });
  requireKnown(upload.status, SCRIPT_UPLOAD_STATUSES, {
    ...context,
    field: 'script_upload.status',
  });
  if (!SHA_256_HEX.test(upload.checksum_sha256)) {
    guardFailure(upload.checksum_sha256, { ...context, field: 'script_upload.checksum_sha256' });
  }
  const size = requireNumberShape(upload.size_bytes, {
    ...context,
    field: 'script_upload.size_bytes',
  });
  if (size <= 0 || !Number.isSafeInteger(size)) {
    guardFailure(upload.size_bytes, { ...context, field: 'script_upload.size_bytes' });
  }
  assertServerTimestamp(upload.created_at, { ...context, field: 'script_upload.created_at' });
  assertServerTimestamp(upload.expires_at, { ...context, field: 'script_upload.expires_at' });
  return upload;
}

export function assertScriptUploadResponse(
  response: ScriptUploadResponse,
  context: GuardContext,
): ScriptUploadResponse {
  assertScriptUpload(response.upload, context);
  if (typeof response.duplicate !== 'boolean') {
    guardFailure(response.duplicate, { ...context, field: 'script_upload.duplicate' });
  }
  return response;
}

export function assertDiscardScriptUploadResponse(
  response: DiscardScriptUploadResponse,
  context: GuardContext,
): DiscardScriptUploadResponse {
  if (typeof response.id !== 'string' || response.id === '') {
    guardFailure(response.id, { ...context, field: 'script_upload.discard.id' });
  }
  if (typeof response.removed !== 'boolean') {
    guardFailure(response.removed, { ...context, field: 'script_upload.discard.removed' });
  }
  return response;
}

export function assertScriptEntry(script: ScriptEntry, context: GuardContext): ScriptEntry {
  const field = (name: string): GuardContext => ({ ...context, field: `script.${name}` });
  if (typeof script.id !== 'string' || script.id === '') {
    guardFailure(script.id, field('id'));
  }
  if (typeof script.name !== 'string' || script.name === '') {
    guardFailure(script.name, field('name'));
  }
  requireKnown(script.language, UPLOAD_ALLOWED_EXTENSIONS, field('language'));
  requireKnown(script.status, SCRIPT_ENTRY_STATUSES, field('status'));
  if (!Array.isArray(script.place_ids)) {
    guardFailure(script.place_ids, field('place_ids'));
  }
  for (const placeId of script.place_ids) {
    if (typeof placeId !== 'number' || !Number.isSafeInteger(placeId) || placeId <= 0) {
      guardFailure(placeId, field('place_ids'));
    }
  }
  if (!SHA_256_HEX.test(script.checksum_sha256)) {
    guardFailure(script.checksum_sha256, field('checksum_sha256'));
  }
  const size = requireNumberShape(script.size_bytes, field('size_bytes'));
  if (size <= 0 || !Number.isSafeInteger(size)) {
    guardFailure(script.size_bytes, field('size_bytes'));
  }
  assertServerTimestamp(script.created_at, field('created_at'));
  assertServerTimestamp(script.updated_at, field('updated_at'));
  return script;
}

export function assertScriptResponse(
  response: ScriptResponse,
  context: GuardContext,
): ScriptResponse {
  assertScriptEntry(response.script, context);
  return response;
}

export function assertScriptListResponse(
  response: ScriptListResponse,
  context: GuardContext,
): ScriptListResponse {
  if (!Array.isArray(response.scripts)) {
    guardFailure(response.scripts, { ...context, field: 'script.list' });
  }
  for (const script of response.scripts) {
    assertScriptEntry(script, context);
  }
  if (response.next_cursor !== null && typeof response.next_cursor !== 'string') {
    guardFailure(response.next_cursor, { ...context, field: 'script.next_cursor' });
  }
  const limit = requireNumberShape(response.limit, { ...context, field: 'script.limit' });
  if (limit <= 0 || !Number.isSafeInteger(limit)) {
    guardFailure(response.limit, { ...context, field: 'script.limit' });
  }
  if (typeof response.has_more !== 'boolean') {
    guardFailure(response.has_more, { ...context, field: 'script.has_more' });
  }
  return response;
}

export function assertKeysystemConfig(
  response: KeysystemConfigResponse,
  context: GuardContext,
): KeysystemConfigResponse {
  const root = requireObjectShape(response, context);
  const config = requireObjectShape(root['config'], { ...context, field: 'keysystem.config' });
  const flow = requireObjectShape(config['flow'], { ...context, field: 'keysystem.flow' });
  if (flow['id'] !== null && typeof flow['id'] !== 'string') {
    guardFailure(flow['id'], { ...context, field: 'keysystem.flow.id' });
  }
  if (typeof flow['name'] !== 'string' || flow['name'] === '') {
    guardFailure(flow['name'], { ...context, field: 'keysystem.flow.name' });
  }
  requireKnown(flow['status'], KEYSYSTEM_STATUSES, { ...context, field: 'keysystem.flow.status' });
  const ttl = requireNumberShape(flow['session_ttl_seconds'], {
    ...context,
    field: 'keysystem.flow.session_ttl_seconds',
  });
  if (!Number.isSafeInteger(ttl) || ttl <= 0) {
    guardFailure(ttl, { ...context, field: 'keysystem.flow.session_ttl_seconds' });
  }
  if (!Array.isArray(config['providers'])) {
    guardFailure(config['providers'], { ...context, field: 'keysystem.providers' });
  }
  for (const entry of config['providers']) {
    const provider = requireObjectShape(entry, { ...context, field: 'keysystem.provider' });
    requireKnown(provider['provider'], KEYSYSTEM_PROVIDERS, {
      ...context,
      field: 'keysystem.provider.provider',
    });
    for (const field of [
      'connection_id',
      'display_name',
      'target_url',
      'publisher_id',
      'callback_url',
    ] as const) {
      if (typeof provider[field] !== 'string') {
        guardFailure(provider[field], { ...context, field: `keysystem.provider.${field}` });
      }
    }
    for (const field of ['enabled', 'anti_bypass_token_set', 'callback_secret_set'] as const) {
      if (typeof provider[field] !== 'boolean') {
        guardFailure(provider[field], { ...context, field: `keysystem.provider.${field}` });
      }
    }
    const minimumSeconds = requireNumberShape(provider['minimum_completion_seconds'], {
      ...context,
      field: 'keysystem.provider.minimum_completion_seconds',
    });
    if (!Number.isSafeInteger(minimumSeconds) || minimumSeconds < 0) {
      guardFailure(minimumSeconds, {
        ...context,
        field: 'keysystem.provider.minimum_completion_seconds',
      });
    }
  }
  return response;
}

export function assertDeleteScriptResponse(
  response: DeleteScriptResponse,
  context: GuardContext,
): DeleteScriptResponse {
  if (typeof response.id !== 'string' || response.id === '') {
    guardFailure(response.id, { ...context, field: 'script.delete.id' });
  }
  if (typeof response.removed !== 'boolean') {
    guardFailure(response.removed, { ...context, field: 'script.delete.removed' });
  }
  return response;
}

function assertLoaderScript(script: LoaderScript, context: GuardContext): LoaderScript {
  if (typeof script.id !== 'string' || script.id === '') {
    guardFailure(script.id, { ...context, field: 'loader.script.id' });
  }
  if (typeof script.name !== 'string' || script.name === '') {
    guardFailure(script.name, { ...context, field: 'loader.script.name' });
  }
  requireKnown(script.language, UPLOAD_ALLOWED_EXTENSIONS, {
    ...context,
    field: 'loader.script.language',
  });
  if (!Array.isArray(script.place_ids) || script.place_ids.length === 0) {
    guardFailure(script.place_ids, { ...context, field: 'loader.script.place_ids' });
  }
  for (const placeId of script.place_ids) {
    if (typeof placeId !== 'number' || !Number.isSafeInteger(placeId) || placeId <= 0) {
      guardFailure(placeId, { ...context, field: 'loader.script.place_ids' });
    }
  }
  if (!SHA_256_HEX.test(script.checksum_sha256)) {
    guardFailure(script.checksum_sha256, { ...context, field: 'loader.script.checksum_sha256' });
  }
  return script;
}

export function assertScriptLoader(
  loader: ScriptLoader,
  context: GuardContext,
): ScriptLoader {
  if (typeof loader.name !== 'string' || loader.name === '') {
    guardFailure(loader.name, { ...context, field: 'loader.name' });
  }
  requireKnown(loader.language, UPLOAD_ALLOWED_EXTENSIONS, {
    ...context,
    field: 'loader.language',
  });
  if (typeof loader.content !== 'string' || loader.content.length === 0) {
    guardFailure(loader.content, { ...context, field: 'loader.content' });
  }
  if (!SHA_256_HEX.test(loader.checksum_sha256)) {
    guardFailure(loader.checksum_sha256, { ...context, field: 'loader.checksum_sha256' });
  }
  const size = requireNumberShape(loader.size_bytes, { ...context, field: 'loader.size_bytes' });
  if (size <= 0 || !Number.isSafeInteger(size)) {
    guardFailure(loader.size_bytes, { ...context, field: 'loader.size_bytes' });
  }
  if (!Array.isArray(loader.scripts) || loader.scripts.length === 0) {
    guardFailure(loader.scripts, { ...context, field: 'loader.scripts' });
  }
  for (const script of loader.scripts) {
    assertLoaderScript(script, context);
  }
  if (typeof loader.unsupported_message !== 'string' || loader.unsupported_message === '') {
    guardFailure(loader.unsupported_message, { ...context, field: 'loader.unsupported_message' });
  }
  assertServerTimestamp(loader.created_at, { ...context, field: 'loader.created_at' });
  return loader;
}

export function assertBuildLoaderResponse(
  response: BuildLoaderResponse,
  context: GuardContext,
): BuildLoaderResponse {
  assertScriptLoader(response.loader, context);
  return response;
}

function assertProtectionStats(stats: ScriptProtectionStats, context: GuardContext): void {
  const counters: Array<[string, number]> = [
    ['original_bytes', stats.original_bytes],
    ['output_bytes', stats.output_bytes],
    ['literals_encrypted', stats.literals_encrypted],
    ['literals_skipped', stats.literals_skipped],
    ['table_entries', stats.table_entries],
    ['names_renamed', stats.names_renamed],
    ['names_skipped', stats.names_skipped],
    ['junk_blocks', stats.junk_blocks],
  ];
  for (const [field, value] of counters) {
    const shape = requireNumberShape(value, { ...context, field: `protection.${field}` });
    if (shape < 0 || !Number.isSafeInteger(shape)) {
      guardFailure(value, { ...context, field: `protection.${field}` });
    }
  }
  if (!Array.isArray(stats.helper_names)) {
    guardFailure(stats.helper_names, { ...context, field: 'protection.helper_names' });
  }
  for (const name of stats.helper_names) {
    if (typeof name !== 'string' || name === '' || name.length > 64) {
      guardFailure(name, { ...context, field: 'protection.helper_names' });
    }
  }
}

export function assertProtectedScript(
  script: ProtectedScript,
  context: GuardContext,
): ProtectedScript {
  if (typeof script.id !== 'string' || script.id === '') {
    guardFailure(script.id, { ...context, field: 'protection.id' });
  }
  if (typeof script.name !== 'string' || script.name === '') {
    guardFailure(script.name, { ...context, field: 'protection.name' });
  }
  requireKnown(script.language, UPLOAD_ALLOWED_EXTENSIONS, {
    ...context,
    field: 'protection.language',
  });
  requireKnown(script.preset, SCRIPT_OBFUSCATION_PRESETS, {
    ...context,
    field: 'protection.preset',
  });
  const seed = requireNumberShape(script.seed, { ...context, field: 'protection.seed' });
  if (
    seed < SCRIPT_OBFUSCATION_LIMITS.minSeed ||
    seed > SCRIPT_OBFUSCATION_LIMITS.maxSeed ||
    !Number.isSafeInteger(seed)
  ) {
    guardFailure(script.seed, { ...context, field: 'protection.seed' });
  }
  if (
    typeof script.content !== 'string' ||
    script.content.length === 0 ||
    script.content.length > SCRIPT_OBFUSCATION_LIMITS.maxOutputBytes
  ) {
    guardFailure(script.content, { ...context, field: 'protection.content' });
  }
  if (!SHA_256_HEX.test(script.checksum_sha256)) {
    guardFailure(script.checksum_sha256, { ...context, field: 'protection.checksum_sha256' });
  }
  const size = requireNumberShape(script.size_bytes, { ...context, field: 'protection.size_bytes' });
  if (size <= 0 || !Number.isSafeInteger(size)) {
    guardFailure(script.size_bytes, { ...context, field: 'protection.size_bytes' });
  }
  assertProtectionStats(script.stats, context);
  if (!Array.isArray(script.warnings) || script.warnings.length > SCRIPT_OBFUSCATION_LIMITS.maxWarnings) {
    guardFailure(script.warnings, { ...context, field: 'protection.warnings' });
  }
  for (const warning of script.warnings) {
    if (typeof warning !== 'string' || warning.length > 400) {
      guardFailure(warning, { ...context, field: 'protection.warnings' });
    }
  }
  assertServerTimestamp(script.created_at, { ...context, field: 'protection.created_at' });
  return script;
}

export function assertObfuscateScriptResponse(
  response: ObfuscateScriptResponse,
  context: GuardContext,
): ObfuscateScriptResponse {
  assertProtectedScript(response.protected_script, context);
  return response;
}

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function assertPercentage(value: unknown, context: GuardContext): number {
  const percent = requireNumberShape(value, context);
  if (percent < 0 || percent > 100) {
    guardFailure(value, context);
  }
  return percent;
}

function assertStatusDay(value: unknown, context: GuardContext): void {
  const day = requireObjectShape(value, context);
  const stamp = day['day'];
  if (typeof stamp !== 'string' || !DAY_KEY_PATTERN.test(stamp)) {
    guardFailure(stamp, { ...context, field: 'status.day' });
  }
  const checks = requireNumberShape(day['checks'], { ...context, field: 'status.day.checks' });
  if (checks < 0 || !Number.isSafeInteger(checks)) {
    guardFailure(day['checks'], { ...context, field: 'status.day.checks' });
  }
  const failures = requireNumberShape(day['failures'], { ...context, field: 'status.day.failures' });
  if (failures < 0 || failures > checks) {
    guardFailure(day['failures'], { ...context, field: 'status.day.failures' });
  }
  assertPercentage(day['uptime_percent'], { ...context, field: 'status.day.uptime_percent' });
}

function assertStatusComponent(value: unknown, context: GuardContext): void {
  const component = requireObjectShape(value, context) as unknown as StatusComponent;
  if (typeof component.id !== 'string' || component.id === '' || component.id.length > 40) {
    guardFailure(component.id, { ...context, field: 'status.component.id' });
  }
  if (typeof component.name !== 'string' || component.name === '') {
    guardFailure(component.name, { ...context, field: 'status.component.name' });
  }
  if (typeof component.description !== 'string') {
    guardFailure(component.description, { ...context, field: 'status.component.description' });
  }
  requireKnown(component.probe, STATUS_PROBES, { ...context, field: 'status.component.probe' });
  requireKnown(component.state, STATUS_STATES, { ...context, field: 'status.component.state' });
  assertPercentage(component.uptime_percent, { ...context, field: 'status.component.uptime' });
  requireNullableNumber(component.latency_ms, { ...context, field: 'status.component.latency' });
  if (component.last_checked_at !== null) {
    assertServerTimestamp(component.last_checked_at, {
      ...context,
      field: 'status.component.last_checked_at',
    });
  }
  if (!Array.isArray(component.days)) {
    guardFailure(component.days, { ...context, field: 'status.component.days' });
  }
  for (const day of component.days) {
    assertStatusDay(day, { ...context, field: 'status.component.days' });
  }
}

function assertStatusIncident(value: unknown, context: GuardContext): void {
  const incident = requireObjectShape(value, context) as unknown as StatusIncident;
  for (const field of ['id', 'component_id', 'component_name', 'title'] as const) {
    if (typeof incident[field] !== 'string' || incident[field] === '') {
      guardFailure(incident[field], { ...context, field: `status.incident.${field}` });
    }
  }
  requireKnown(incident.state, STATUS_INCIDENT_STATES, {
    ...context,
    field: 'status.incident.state',
  });
  requireKnown(incident.severity, STATUS_SEVERITIES, { ...context, field: 'status.incident.severity' });
  assertServerTimestamp(incident.started_at, { ...context, field: 'status.incident.started_at' });
  assertServerTimestamp(incident.updated_at, { ...context, field: 'status.incident.updated_at' });
  if (incident.resolved_at !== null) {
    assertServerTimestamp(incident.resolved_at, { ...context, field: 'status.incident.resolved_at' });
  }
}

export function assertStatusResponse(payload: unknown, context: GuardContext): StatusResponse {
  const value = requireObjectShape(payload, context);
  const response = value as unknown as StatusResponse;
  for (const field of ['service', 'version', 'environment'] as const) {
    if (typeof response[field] !== 'string') {
      guardFailure(response[field], { ...context, field: `status.${field}` });
    }
  }
  assertServerTimestamp(response.generated_at, { ...context, field: 'status.generated_at' });
  const windowDays = requireNumberShape(response.window_days, { ...context, field: 'status.window_days' });
  if (windowDays < 1 || windowDays > 365 || !Number.isSafeInteger(windowDays)) {
    guardFailure(response.window_days, { ...context, field: 'status.window_days' });
  }
  requireKnown(response.overall, STATUS_OVERALL_STATES, { ...context, field: 'status.overall' });
  if (response.last_probe_at !== null) {
    assertServerTimestamp(response.last_probe_at, { ...context, field: 'status.last_probe_at' });
  }
  if (!Array.isArray(response.components)) {
    guardFailure(response.components, { ...context, field: 'status.components' });
  }
  for (const component of response.components) {
    assertStatusComponent(component, { ...context, field: 'status.components' });
  }
  if (!Array.isArray(response.incidents)) {
    guardFailure(response.incidents, { ...context, field: 'status.incidents' });
  }
  for (const incident of response.incidents) {
    assertStatusIncident(incident, { ...context, field: 'status.incidents' });
  }
  return response;
}
